import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import { ExternalLink, LayoutGrid, Pause, ScrollText } from 'lucide-react';

import {
    GetMicrostoreColumnGroupQuery,
    MicrostoreElementState,
    useGetMicrostoreColumnGroupQuery,
    useUpdateMicrostoreColumnGroupMutation,
} from 'data/__generated__';

import { Loader } from 'components/Loader';
import {
    ActionsAndSave,
    EntityPageBreadcrumb,
    PageActions,
    PageHeader,
    type PageActionGroup,
} from 'components/PageHeader';
import { SectionContainer, ColumnsSectionContainer, SectionColumn } from 'components/DetailsView/Section';
import { DetailFormValue } from 'components/DetailsView/DetailFormValue';
import { DetailDisplayValue } from 'components/DetailsView/DetailDisplayValue';
import { DetailFormSelect } from 'components/DetailsView/DetailFormSelect';
import { DetailValue } from 'components/DetailsView';
import { CopyValue } from 'components/CopyValue';
import { AuditLogs } from 'components/AuditLogs';
import {
    Tree,
    TreeActions,
    TreeLabel,
    TreeLeaf,
    TreeNode,
    TreeNodeChildren,
    TreeNodeTrigger,
} from '@/components/ui/tree';
import { ColumnNodeActions } from 'pages/Sites/SiteMicrostoreColumnGroups/ColumnNodeActions';
import { ColumnGroupNodeActions } from 'pages/Sites/SiteMicrostoreColumnGroups/ColumnGroupNodeActions';

type ParamTypes = {
    columnGroupId: string;
};

type ColumnGroup = NonNullable<GetMicrostoreColumnGroupQuery['microstoreColumnGroup']>;

type FormValues = {
    name: string;
    positionInSite: number;
    avaSalePlaceLocationId: number | null;
};

const STATE_BADGE: Record<MicrostoreElementState, string> = {
    [MicrostoreElementState.Regular]: 'bg-emerald-100 text-emerald-900 border-emerald-300',
    [MicrostoreElementState.Disabled]: 'bg-amber-100 text-amber-900 border-amber-300',
    [MicrostoreElementState.Archived]: 'bg-zinc-100 text-zinc-600 border-zinc-300',
};

export const ColumnGroupPage = () => {
    const { columnGroupId = '' } = useParams<ParamTypes>();

    const { data, loading, error, refetch } = useGetMicrostoreColumnGroupQuery({
        variables: { columnGroupId },
    });

    if (loading) {
        return (
            <Container>
                <CenteredLoader>
                    <Loader />
                </CenteredLoader>
            </Container>
        );
    }

    if (error || !data?.microstoreColumnGroup) {
        throw new Error('Une erreur est survenue lors de la récupération du groupe de colonnes');
    }

    return <ColumnGroupForm columnGroup={data.microstoreColumnGroup} onMutated={() => refetch()} />;
};

const ColumnGroupForm = ({ columnGroup, onMutated }: { columnGroup: ColumnGroup; onMutated: () => void }) => {
    const site = columnGroup.site;
    const totem = site.totem;
    const organization = totem.organization;

    const methods = useForm<FormValues>({
        defaultValues: {
            name: columnGroup.name,
            positionInSite: columnGroup.positionInSite,
            avaSalePlaceLocationId: columnGroup.avaSalePlaceLocationId ?? null,
        },
    });

    const {
        formState: { isDirty },
        handleSubmit,
        reset,
    } = methods;

    const [updateMicrostoreColumnGroup, { loading: updateLoading }] = useUpdateMicrostoreColumnGroupMutation();

    const handleFormSubmit = handleSubmit(async (values) => {
        const { data } = await updateMicrostoreColumnGroup({
            variables: {
                microstoreColumnGroupId: columnGroup._id,
                updateMicrostoreColumnGroupInput: {
                    name: values.name,
                    positionInSite: Number(values.positionInSite),
                    avaSalePlaceLocationId: values.avaSalePlaceLocationId
                        ? Number(values.avaSalePlaceLocationId)
                        : null,
                },
            },
        });

        if (!data) {
            throw new Error('Une erreur est survenue lors de la modification du groupe de colonnes');
        }

        const {
            updateMicrostoreColumnGroupMutation: { errors },
        } = data;

        if (errors.length) {
            errors.forEach((error, index) => {
                toast.error(<span key={index}>Erreur : {error}</span>);
            });
            return;
        }

        toast.success('Le groupe de colonnes a bien été modifié !');
        reset(values);
    });

    const actionGroups: PageActionGroup[] = [
        {
            label: 'Outils',
            items: [
                {
                    label: 'Éditeur legacy (planogramme)',
                    icon: <ScrollText />,
                    to: `/microstore-column-groups/${site._id}`,
                },
                { label: 'Planogramme visuel', icon: <LayoutGrid />, to: `/planogram/${site._id}` },
                { label: 'Ouvrir le site', icon: <ExternalLink />, to: `/site/${site._id}` },
            ],
        },
    ];

    const sortedColumns = [...columnGroup.columns].sort((a, b) => a.positionInColumnGroup - b.positionInColumnGroup);

    const positionInSiteOptions = [...site.microstoreColumnGroups]
        .sort((a, b) => a.positionInSite - b.positionInSite)
        .map((group) => ({
            value: group.positionInSite,
            label: `${group.positionInSite + 1} — ${group.name} (${group.columns.length} colonne${
                group.columns.length > 1 ? 's' : ''
            })`,
        }));

    return (
        <Container data-test="column-group-page">
            <Helmet title={`${columnGroup.name} · TOTADMIN`} />
            <FormProvider {...methods}>
                <Form>
                    <PageHeader>
                        <EntityPageBreadcrumb
                            organization={organization}
                            totem={totem}
                            site={site}
                            columnGroup={columnGroup}
                        />
                        <PageActions>
                            <ActionsAndSave
                                actionGroups={actionGroups}
                                onSave={handleFormSubmit}
                                saveDisabled={!isDirty}
                                saveLoading={updateLoading}
                                saveDataTest="column-group-save-button"
                            />
                        </PageActions>
                    </PageHeader>
                    <Content>
                        <ScrollableContent>
                            <SectionContainer title="Informations générales" isInitiallyOpen>
                                <ColumnsSectionContainer numberOfColumns={2}>
                                    <SectionColumn>
                                        <CopyValue value={columnGroup._id} />
                                        <DetailFormValue
                                            label="Nom"
                                            defaultValue={columnGroup.name}
                                            data-test="column-group-name-input"
                                            {...methods.register('name', { required: true })}
                                        />
                                        <DetailFormSelect<number>
                                            label="Position dans le site"
                                            name="positionInSite"
                                            defaultValue={columnGroup.positionInSite}
                                            options={positionInSiteOptions}
                                            required
                                            dataTest="column-group-position-select"
                                        />
                                    </SectionColumn>
                                    <SectionColumn>
                                        <DetailFormValue
                                            label="AVA Sale Place Location ID"
                                            type="number"
                                            step="1"
                                            defaultValue={columnGroup.avaSalePlaceLocationId ?? null}
                                            data-test="column-group-ava-input"
                                            {...methods.register('avaSalePlaceLocationId', {
                                                valueAsNumber: true,
                                                setValueAs: (v) => (v === '' || v === null ? null : Number(v)),
                                            })}
                                        />
                                        <DetailDisplayValue
                                            label="Odoo Column Group ID"
                                            value={columnGroup.odooMicrostoreColumnGroupId ?? '—'}
                                        />
                                        <DetailValue label="Site" value={site.name} />
                                    </SectionColumn>
                                </ColumnsSectionContainer>
                            </SectionContainer>

                            <SectionContainer title={`Colonnes (${sortedColumns.length})`} isInitiallyOpen>
                                <Tree>
                                    <TreeNode defaultOpen>
                                        <TreeNodeTrigger>
                                            <TreeLabel>
                                                <span className="font-semibold">{columnGroup.name}</span>
                                                <span className="text-xs text-muted-foreground">
                                                    {sortedColumns.length} colonne
                                                    {sortedColumns.length > 1 ? 's' : ''}
                                                </span>
                                            </TreeLabel>
                                            <TreeActions>
                                                <ColumnGroupNodeActions
                                                    columnGroup={columnGroup}
                                                    onMutated={onMutated}
                                                />
                                            </TreeActions>
                                        </TreeNodeTrigger>
                                        <TreeNodeChildren>
                                            {sortedColumns.length === 0 ? (
                                                <li className="px-1.5 py-1 text-xs text-muted-foreground">
                                                    Aucune colonne
                                                </li>
                                            ) : (
                                                sortedColumns.map((column) => (
                                                    <TreeLeaf key={column._id}>
                                                        <TreeLabel>
                                                            <span className="w-7 shrink-0 font-mono text-xs text-muted-foreground">
                                                                #{column.positionInColumnGroup + 1}
                                                            </span>
                                                            <span className="truncate font-medium">{column.name}</span>
                                                            <span className="hidden truncate text-xs text-muted-foreground sm:inline">
                                                                {column.microstoreColumnTemplateName}
                                                            </span>
                                                            <StateBadge className={STATE_BADGE[column.state]}>
                                                                {column.state}
                                                            </StateBadge>
                                                            {column.isPaused ? (
                                                                <span className="inline-flex items-center gap-0.5 rounded-full border border-amber-300 bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-900">
                                                                    <Pause className="size-2.5" />
                                                                    En pause
                                                                </span>
                                                            ) : null}
                                                        </TreeLabel>
                                                        <TreeActions>
                                                            <ColumnNodeActions
                                                                columnId={column._id}
                                                                columnName={column.name}
                                                                isPaused={column.isPaused}
                                                            />
                                                        </TreeActions>
                                                    </TreeLeaf>
                                                ))
                                            )}
                                        </TreeNodeChildren>
                                    </TreeNode>
                                </Tree>
                            </SectionContainer>

                            <SectionContainer title="Audit logs" isInitiallyOpen={false}>
                                <AuditLogs collectionDocumentId={columnGroup._id} />
                            </SectionContainer>
                        </ScrollableContent>
                    </Content>
                </Form>
            </FormProvider>
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const Form = styled.form`
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
`;

const Content = styled.div`
    flex: 1;
    overflow: hidden;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const ScrollableContent = styled.div`
    padding: 15px;
    width: 100%;
    height: 100%;
    overflow-y: auto;

    & > :not(:first-child) {
        margin-top: 10px;
    }
`;

const StateBadge = styled.span`
    border-radius: 9999px;
    border-width: 1px;
    padding: 1px 8px;
    font-size: 10px;
    font-weight: 500;
`;

const CenteredLoader = styled.div`
    display: flex;
    flex: 1;
    align-items: center;
    justify-content: center;
`;
